'use client';

import { TabConverter } from "./TabConverter";
import { event, t, trackEvent } from "@/lib";
import { useEffect, useRef, useState } from "react";
import { useHarmonicaAudio } from "@/hooks/useHarmonicaAudio";

interface PartitionProps {
    tablatureId?: string;
    partition: string;
    dictionary: any;
    title?: string;
    isAuthenticated?: boolean;
}

export const Partition = ({ tablatureId, partition, dictionary, title, isAuthenticated }: PartitionProps) => {
    const partitionTitle = title || t(dictionary, 'tablatures.partition');
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(800);

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                const width = containerRef.current.offsetWidth;
                setContainerWidth(width > 0 ? width : 800);
            }
        };

        // Initial width calculation
        updateWidth();

        // Update on window resize
        window.addEventListener('resize', updateWidth);

        // Cleanup
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    if (!partition?.trim()) {
        return (
            <div
                ref={containerRef}
                className="min-h-[470px] bg-white border border-[var(--color-gray-200)] rounded-lg overflow-hidden flex items-center justify-center"
            >
                <div className="text-center">
                    <i className="fa-solid fa-music text-4xl mx-auto mb-3 text-[var(--color-gray-400)] block" aria-hidden="true" />
                    <p className="text-[var(--color-gray-600)]">
                        {t(dictionary, 'tablatures.no_partition')}
                    </p>
                </div>
            </div>
        );
    }

    const [viewMode, setViewMode] = useState<'standard' | 'linear'>('standard');
    const [isLinear, setIsLinear] = useState(false); // Helper for toggle
    const [isFullscreen, setIsFullscreen] = useState(false);

    const { play, stop, isPlaying, isReady, currentIndices, tempo, setTempo } = useHarmonicaAudio();

    // Generate HTML based on view mode
    const partitionHTML = viewMode === 'standard'
        ? TabConverter.toNewHTML(partition, containerWidth, currentIndices)
        : TabConverter.toLinearHTML(partition, currentIndices);

    // Determine current action for display
    let currentActionText = '';
    let currentNoteText = '';
    if (currentIndices.length > 0) {
        // Find the first note being played
        const notes = TabConverter.toNotes(partition);
        // We use the first index effectively to determine Draw/Blow state
        const firstIndex = currentIndices[0];
        const currentNote = notes.find(n => (n as any).index === firstIndex);
        if (currentNote) {
            const isDraw = currentNote.sign === '-';
            currentActionText = isDraw ? String(t(dictionary, 'audio.action_draw')) : String(t(dictionary, 'audio.action_blow'));
            currentNoteText = TabConverter.fromNoteToText(currentNote);
        }
    }

    const handlePlay = () => {
        const notes = TabConverter.toNotes(partition);
        play(notes);
        if (tablatureId) {
            trackEvent('partition_play', {
                id: String(tablatureId),
            });
        }
    };

    const toggleViewMode = () => {
        const newMode = viewMode === 'standard' ? 'linear' : 'standard';
        setViewMode(newMode);
        setIsLinear(newMode === 'linear');

        if (tablatureId) {
            trackEvent('partition_view_mode', {
                id: String(tablatureId),
                mode: newMode,
            });
        }
    };

    // Fullscreen render content
    const renderContent = (fullscreen: boolean) => (
        <div className={`flex flex-col gap-4 ${fullscreen ? 'h-full' : ''}`}>
            <style dangerouslySetInnerHTML={{
                __html: `
                .highlighted .topElement,
                .highlighted .bottomElement,
                .highlighted .value {
                    color: #d97706 !important; /* amber-600 */
                    font-weight: 700;
                    /* transform: scale(1.1); Removed scale per user request */
                    transition: all 0.1s ease;
                }
            `}} />

            <div
                ref={containerRef}
                className={`bg-white border border-[var(--color-gray-200)] rounded-lg overflow-hidden relative ${fullscreen ? 'flex-1 overflow-y-auto' : 'min-h-[470px]'}`}
                style={viewMode === 'standard' ? {
                    backgroundImage: 'linear-gradient(to right, var(--color-gray-100) 0px, transparent 1px), linear-gradient(var(--color-gray-200) 1px, transparent 1px)',
                    backgroundSize: '50px 93px'
                } : {
                    background: 'none'
                }}
                role="img"
                aria-labelledby="partition-title"
                aria-describedby="partition-description"
            >
                {/* Hidden title for screen readers */}
                <h3 id="partition-title" className="sr-only">
                    {partitionTitle}
                </h3>

                {/* Hidden description for screen readers */}
                <div id="partition-description" className="sr-only">
                    {t(dictionary, 'tablatures.partition_description', {
                        title: partitionTitle
                    })}
                </div>

                <div
                    className={`tablature-lines ${viewMode === 'standard' ? '' : 'p-6 leading-loose'}`}
                    style={{
                        width: '100%',
                        // Use block for natural text wrapping in linear mode, consistent with "traditional view"
                        display: 'block',
                        // gap only works for flex/grid, so we rely on leading-loose and inline-block margins
                    }}
                    role="presentation"
                >
                    {partitionHTML}
                </div>

                {/* Fallback text content for accessibility */}
                <div className="sr-only">
                    <h4>{t(dictionary, 'tablatures.partition_text_version')}</h4>
                    <pre className="whitespace-pre-wrap font-mono text-sm">
                        {partition}
                    </pre>
                </div>
            </div>

            {/* Audio Player Controls */}
            <div className={`bg-gray-50 border border-gray-200 rounded-lg p-3 flex flex-wrap items-center gap-4 justify-between transition-all relative ${isPlaying ? 'border-amber-200 bg-amber-50' : ''}`}>

                {/* Member Gate Overlay */}
                {!isAuthenticated && (
                    <div className="absolute inset-0 z-10 bg-white/60 backdrop-blur-[1px] flex items-center justify-center rounded-lg border border-gray-200">
                        <div className="bg-white px-4 py-2 rounded-lg shadow-lg border border-gray-200 flex items-center gap-3">
                            <i className="fa-solid fa-lock text-amber-500"></i>
                            <span className="text-sm font-medium text-gray-700">
                                {t(dictionary, 'audio.player_login_required')}
                            </span>
                            <a href="/login" className="text-xs bg-secondary-600 text-white px-3 py-1.5 rounded-md hover:bg-secondary-700 font-bold transition-colors">
                                {t(dictionary, 'auth.links_login')}
                            </a>
                        </div>
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-3">
                    {/* Beta Banner - Moved here */}
                    <span className="bg-secondary-100 text-secondary-700 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wide mr-1 border border-secondary-200" title={String(t(dictionary, 'audio.player_beta_warning'))}>
                        {t(dictionary, 'audio.player_beta')}
                    </span>

                    <button
                        type="button"
                        onClick={isPlaying ? stop : handlePlay}
                        disabled={!isAuthenticated}
                        className={`flex items-center gap-2 px-5 py-2 rounded-lg font-medium transition-colors shadow-sm ${isPlaying
                            ? 'bg-white text-red-600 border border-red-200 hover:bg-red-50'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            } ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isPlaying ? (
                            <>
                                <i className="fa-solid fa-stop"></i>
                                {t(dictionary, 'audio.stop')}
                            </>
                        ) : (
                            <>
                                <i className="fa-solid fa-play"></i>
                                {t(dictionary, 'audio.play')}
                            </>
                        )}
                    </button>

                    <div className={`flex items-center gap-2 text-sm text-gray-700 bg-white px-3 py-2 rounded-lg border border-gray-300 shadow-sm ${!isAuthenticated ? 'opacity-50' : ''}`}>
                        <i className="fa-solid fa-gauge-high text-gray-400"></i>
                        <span className="font-medium text-xs uppercase text-gray-500">{t(dictionary, 'audio.tempo')}</span>
                        <select
                            value={tempo}
                            onChange={(e) => setTempo(Number(e.target.value))}
                            disabled={!isAuthenticated}
                            className="bg-transparent border-none focus:ring-0 cursor-pointer font-semibold p-0 text-gray-900"
                        >
                            <option value={1200}>x0.25</option>
                            <option value={600}>x0.5</option>
                            <option value={400}>x1</option>
                            <option value={200}>x2</option>
                        </select>
                    </div>

                    {/* Current Action Display */}
                    {isPlaying && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-amber-100 text-amber-800 rounded-lg border border-amber-200 animate-pulse">
                            <span className="font-bold text-lg">{currentActionText}</span>
                            <span className="font-mono text-xl font-black">{currentNoteText}</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {/* View Mode Toggle */}
                    <button
                        type="button"
                        onClick={toggleViewMode}
                        className="flex items-center gap-2 px-3 py-2 bg-white text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                        title={viewMode === 'standard' ? String(t(dictionary, 'audio.view_mode_linear')) : String(t(dictionary, 'audio.view_mode_standard'))}
                    >
                        <i className={`fa-solid ${viewMode === 'standard' ? 'fa-align-justify' : 'fa-music'}`}></i>
                        <span className="hidden sm:inline">
                            {viewMode === 'standard' ? 'Linear' : 'Standard'}
                        </span>
                    </button>

                    {/* Fullscreen Toggle */}
                    {!fullscreen && (
                        <button
                            type="button"
                            onClick={() => isAuthenticated && setIsFullscreen(true)}
                            disabled={!isAuthenticated}
                            className={`flex items-center gap-2 px-3 py-2 bg-white text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={String(t(dictionary, 'audio.fullscreen'))}
                        >
                            <i className="fa-solid fa-expand"></i>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <>
            {renderContent(false)}

            {/* Fullscreen Modal rely on @ui/composites/Modal if available or simple fixed overlay */}
            {isFullscreen && isAuthenticated && (
                <div className="fixed inset-0 z-50 bg-white p-4 flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold">{partitionTitle}</h2>
                        <button
                            type="button"
                            onClick={() => setIsFullscreen(false)}
                            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full"
                        >
                            <i className="fa-solid fa-xmark text-xl"></i>
                        </button>
                    </div>
                    <div className="flex-1 overflow-hidden flex flex-col">
                        {renderContent(true)}
                    </div>
                </div>
            )}
        </>
    );
};